<template>
  <div class="overflow-x-clip bg-neutral-50">
    <Head v-if="specialist" :title="specialist.full_name" />

    <div v-if="specialist" class="mx-auto w-full min-w-0 max-w-[1200px] px-4 py-8 sm:px-6 lg:px-0">
      <nav class="mb-8 flex min-w-0 flex-wrap items-center gap-1 text-sm" aria-label="Breadcrumb">
        <Link :href="homeHref" class="shrink-0 text-neutral-700 hover:text-[#2ea8cf]">
          {{ $t('breadcrumbs.home') }}
        </Link>
        <span class="shrink-0 text-neutral-300" aria-hidden="true">/</span>
        <Link :href="specialistsIndexHref" class="shrink-0 text-neutral-700 hover:text-[#2ea8cf]">
          {{ $t('breadcrumbs.specialists') }}
        </Link>
        <span class="shrink-0 text-neutral-300" aria-hidden="true">/</span>
        <span class="min-w-0 break-words text-[#2ea8cf]">{{ specialist.full_name }}</span>
      </nav>

      <div class="grid min-w-0 grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start lg:gap-8">
        <!-- Left column: profile + content cards -->
        <div class="flex min-w-0 flex-col gap-6">
          <!-- 1. Specialist profile card -->
          <article
            id="specjalista"
            class="scroll-mt-[48px] min-w-0 overflow-hidden rounded-xl bg-white shadow-[0px_6px_15px_-2px_rgba(16,24,40,0.08)] lg:scroll-mt-[64px]"
          >
            <div class="flex min-w-0 flex-col gap-3 px-4 pt-3 pb-4 lg:px-6 lg:pb-0">
              <div v-if="specialist.specialisations?.length" class="flex min-w-0 flex-wrap gap-2">
                <Link
                  v-for="specialisation in specialist.specialisations"
                  :key="specialisation.id"
                  :href="specialisationHref(specialisation)"
                  class="max-w-full no-underline transition-opacity hover:opacity-80"
                >
                  <SpecialisationBadge
                    :name="specialisation.name"
                    :site-context="specialisation.site_context"
                  />
                </Link>
              </div>

              <div class="flex min-w-0 items-center gap-3">
                <div
                  v-if="specialist.photo_url"
                  class="size-24 shrink-0 overflow-hidden rounded-t-lg bg-neutral-200 lg:size-[128px]"
                >
                  <img
                    :src="specialist.photo_url"
                    :alt="specialist.full_name"
                    class="size-full object-cover"
                  >
                </div>
                <div class="min-w-0 flex-1">
                  <p v-if="specialist.title" class="text-base leading-6 text-neutral-500">
                    {{ specialist.title }}
                  </p>
                  <h1 class="break-words text-xl font-semibold leading-7 tracking-[-0.02em] text-neutral-900">
                    {{ specialist.full_name }}
                  </h1>
                </div>
              </div>
            </div>
          </article>

          <button
            v-if="hasBookableServices"
            type="button"
            class="flex h-12 w-full items-center justify-center gap-2 rounded-lg px-5 py-3.5 text-base font-semibold leading-[18px] text-white shadow-[0px_2px_3px_rgba(16,24,40,0.06)] transition-opacity hover:opacity-90 lg:hidden"
            :style="{ backgroundImage: brandGradient }"
            @click="openBookingModal"
          >
            {{ $t('specialists.book_appointment_with_specialist') }}
          </button>

          <!-- 2. O specjaliście -->
          <section
            v-if="hasAboutSection"
            id="o-specjaliscie"
            class="scroll-mt-[48px] flex min-w-0 flex-col gap-3 rounded-xl bg-white p-4 shadow-[0px_6px_7.5px_rgba(16,24,40,0.08)] lg:scroll-mt-[64px] lg:p-6"
          >
            <h2 class="text-sm font-semibold uppercase tracking-[1px] text-neutral-500">
              {{ $t('specialists.about_heading') }}
            </h2>

            <ExpandableText
              v-if="specialist.description"
              :text="specialist.description"
            />

            <template v-if="specialist.languages?.length">
              <hr class="border-t border-dashed border-neutral-200">
              <h3 class="text-sm font-semibold uppercase tracking-[1px] text-neutral-500">
                {{ $t('specialists.languages') }}
              </h3>
              <LanguagesList :languages="specialist.languages" />
            </template>

            <template v-if="specialist.important_information?.length">
              <hr class="border-t border-dashed border-neutral-200">
              <h3 class="text-sm font-semibold uppercase tracking-[1px] text-neutral-500">
                {{ $t('specialists.important_information') }}
              </h3>
              <ul class="flex flex-col gap-3">
                <li
                  v-for="(info, index) in specialist.important_information"
                  :key="index"
                  class="flex min-w-0 gap-2 text-base leading-6 text-neutral-900"
                >
                  <Icon
                    name="warning"
                    class="mt-0.5 size-5 shrink-0 text-amber-500"
                    aria-hidden="true"
                  />
                  <span class="min-w-0 break-words whitespace-pre-line">{{ info }}</span>
                </li>
              </ul>
            </template>
          </section>

          <!-- 3. Leczone choroby -->
          <section
            v-if="specialist.treated_diseases?.length"
            id="leczone-choroby"
            class="scroll-mt-[48px] flex min-w-0 flex-col gap-3 rounded-xl bg-white p-4 shadow-[0px_6px_7.5px_rgba(16,24,40,0.08)] lg:scroll-mt-[64px] lg:p-6"
          >
            <h2 class="text-sm font-semibold uppercase tracking-[1px] text-neutral-500">
              {{ $t('specialists.treated_diseases') }}
            </h2>
            <ul class="flex flex-col gap-3">
              <li
                v-for="(disease, index) in specialist.treated_diseases"
                :key="index"
                class="flex min-w-0 gap-2 text-base leading-6 text-neutral-900"
              >
                <Icon
                  name="check"
                  class="mt-0.5 size-5 shrink-0 text-green-600"
                  aria-hidden="true"
                />
                <span class="min-w-0 break-words">{{ disease }}</span>
              </li>
            </ul>
          </section>

          <!-- 4. Cennik -->
          <SpecialistPriceList
            v-if="specialist.bookable_services?.length"
            :services="specialist.bookable_services"
            :selected-uuid="selectedServiceUuid"
            :requires-prepayment="Boolean(specialist.requires_prepayment)"
            @select="onSelectService"
          />

          <!-- 5. Media -->
          <section
            v-if="specialist.media?.length"
            id="media"
            class="scroll-mt-[48px] flex min-w-0 flex-col gap-3 rounded-xl bg-white p-4 shadow-[0px_6px_7.5px_rgba(16,24,40,0.08)] lg:scroll-mt-[64px] lg:p-6"
          >
            <h2 class="text-sm font-semibold uppercase tracking-[1px] text-neutral-500">
              {{ $t('specialists.media') }}
            </h2>
            <ul class="flex flex-col gap-3">
              <li v-for="(link, index) in specialist.media" :key="index" class="min-w-0">
                <a
                  :href="link"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="break-all text-base leading-6 text-link hover:underline"
                >
                  {{ link }}
                </a>
              </li>
            </ul>
          </section>

          <!-- 6. FAQ -->
          <section
            v-if="specialist.faq?.length"
            id="faq"
            class="scroll-mt-[48px] flex min-w-0 flex-col gap-3 rounded-xl bg-white p-4 shadow-[0px_6px_7.5px_rgba(16,24,40,0.08)] lg:scroll-mt-[64px] lg:p-6"
          >
            <h2 class="text-sm font-semibold uppercase tracking-[1px] text-neutral-500">
              {{ $t('faq.title') }}
            </h2>
            <div class="flex min-w-0 flex-col gap-3">
              <details
                v-for="(item, index) in specialist.faq"
                :key="index"
                class="group min-w-0 rounded-lg border border-neutral-200 bg-white p-4"
              >
                <summary
                  class="flex cursor-pointer list-none items-center justify-between gap-3 text-base font-medium leading-6 text-neutral-900"
                >
                  <span class="min-w-0 break-words">{{ item.question }}</span>
                  <svg
                    class="size-5 shrink-0 text-neutral-500 transition-transform group-open:rotate-180"
                    viewBox="0 0 20 20"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M5 7.5 10 12.5 15 7.5"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </summary>
                <p
                  v-if="item.answer"
                  class="mt-4 break-words border-t border-neutral-100 pt-4 text-base leading-6 text-neutral-700 whitespace-pre-line"
                >
                  {{ item.answer }}
                </p>
              </details>
            </div>
          </section>
        </div>

        <!-- Right column: booking panel (desktop only) -->
        <aside
          id="umow-wizyte"
          class="hidden min-w-0 scroll-mt-[48px] lg:block lg:sticky lg:self-start lg:scroll-mt-[64px] transition-[top] duration-300 ease-out"
          :style="{ top: `${stickyPanelTop}px` }"
        >
          <SpecialistBookingPanel
            v-model="selectedServiceUuid"
            :specialist-slug="specialist.slug"
            :bookable-services="specialist.bookable_services || []"
            :default-services="specialist.default_services || []"
          />
        </aside>
      </div>

      <BookingSlideOver
        :open="bookingModalOpen"
        :title="$t('booking.heading')"
        :subtitle="specialist.full_name"
        @close="bookingModalOpen = false"
      >
        <SpecialistBookingPanel
          v-if="bookingModalOpen"
          v-model="selectedServiceUuid"
          :specialist-slug="specialist.slug"
          :bookable-services="specialist.bookable_services || []"
          :default-services="specialist.default_services || []"
          :show-header="false"
          compact
          flush
        />
      </BookingSlideOver>
    </div>

    <div v-else class="mx-auto max-w-[1200px] px-4 py-16 text-center text-neutral-500">
      {{ $t('specialists.not_found') }}
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3';
import SpecialisationBadge from '@/Shared/SpecialisationBadge.vue';
import LanguagesList from '@/Shared/LanguagesList.vue';
import ExpandableText from '@/Shared/ExpandableText.vue';
import SpecialistBookingPanel from '@/Shared/SpecialistBookingPanel.vue';
import SpecialistPriceList from '@/Shared/SpecialistPriceList.vue';
import BookingSlideOver from '@/Shared/BookingSlideOver.vue';
import Icon from '@/Shared/Icon.vue';
import { stickyPanelTop } from '@/utils/stickyMainHeader';
import { BRAND_GRADIENT } from '@/utils/siteThemeColors';

// Below lg, booking opens in a slide-over modal instead of an inline panel.
const LG_BREAKPOINT = 1024;

export default {
  name: 'SpecialistShow',
  components: { Head, Link, SpecialisationBadge, LanguagesList, ExpandableText, SpecialistBookingPanel, SpecialistPriceList, BookingSlideOver, Icon },
  props: {
    specialist:         { type: Object, default: null },
    // eslint-disable-next-line vue/prop-name-casing
    site_context:       { type: String, default: null },
    // eslint-disable-next-line vue/prop-name-casing
    site_context_label: { type: String, default: '' },
  },
  data() {
    return {
      selectedServiceUuid: null,
      bookingModalOpen: false,
      brandGradient: BRAND_GRADIENT,
    };
  },
  computed: {
    stickyPanelTop() {
      return stickyPanelTop.value;
    },
    effectiveSiteContext() {
      return this.site_context || 'code_medical_center';
    },
    homeHref() {
      const ctx = this.effectiveSiteContext;
      if (ctx && ctx !== 'code_medical_center' && this.$routes[ctx]) {
        return this.$routes[ctx]();
      }
      return '/';
    },
    specialistsIndexHref() {
      const ctx = this.effectiveSiteContext;
      const routeName = `${ctx}_specialists`;
      if (ctx && ctx !== 'code_medical_center' && this.$routes[routeName]) {
        return this.$routes[routeName]();
      }
      return this.$routes.specialists_index();
    },
    hasAboutSection() {
      return Boolean(
        this.specialist?.description
        || this.specialist?.languages?.length
        || this.specialist?.important_information?.length,
      );
    },
    hasBookableServices() {
      return Boolean(this.specialist?.bookable_services?.length);
    },
  },
  methods: {
    openBookingModal() {
      this.bookingModalOpen = true;
    },
    specialisationHref(specialisation) {
      return this.$routes.specialisation(specialisation.slug);
    },
    onSelectService(remoteUuid) {
      if (!remoteUuid) return;

      this.selectedServiceUuid = remoteUuid;

      if (window.innerWidth < LG_BREAKPOINT) {
        this.bookingModalOpen = true;
      }
    },
  },
};
</script>
